import { Fragment } from 'react';
import { createElement } from 'react';
import { BlockNode } from './nodeRenderer/BlockNode.js';
import { HTMLNode } from './nodeRenderer/HTMLNode.js';
import { HTMLBoundaryNode } from './nodeRenderer/HTMLBoundaryNode.js';
let key = 0;
/**
 * Node in the DOM tree.
 * This node has been scanned by Smartfire Render and can be mapped to a Smartfire render or displayed in the DOM.
 *
 */
export class BlockEngineNode {
    constructor({ parent, node, }) {
        /**
         * List of children of this node.
         */
        this.children = [];
        this.parent = parent;
        this.domNode = node;
    }
    /**
     * Check if this node is a Smartfire Component node.
     * This is true if the node is an HTMLElement and is a tag that starts with smartfire-component or has the class smartfire-component.
     */
    get isSmartfireComponentNode() {
        return this.domNode instanceof HTMLElement &&
            (this.domNode.tagName.toLowerCase().startsWith('smartfire-component') || this.domNode.classList.contains('smartfire-component'));
    }
    /**
     * Check if this node is a Smartfire HTML Boundary node.
     * This is true if the node is an HTMLElement and is a tag that starts with smartfire-html-boundary or has the class smartfire-html-boundary.
     */
    get isHTMLBoundaryNode() {
        return this.domNode instanceof HTMLElement &&
            (this.domNode.tagName.toLowerCase().startsWith('smartfire-html-boundary') || this.domNode.classList.contains('smartfire-html-boundary'));
    }
    /**
     * Check if this node is a DOM Node.
     * This is true if the node is not a Smartfire Component node and not a Smartfire HTML Boundary node.
     */
    get isDOMNode() {
        return !this.isHTMLBoundaryNode && !this.isSmartfireComponentNode;
    }
    /**
     * Hydrate this node.
     * This will scan the DOM Node and create children nodes.
     * If this node is a Smartfire Component node, it will also extract the Smartfire Block name and props.
     */
    hydrate() {
        var _a;
        if (this.domNode instanceof HTMLElement) {
            const children = Array.from(this.domNode.childNodes);
            for (const child of children) {
                const node = new BlockEngineNode({
                    parent: this,
                    node: child
                });
                node.hydrate();
                this.children.push(node);
            }
        }
        if (this.isSmartfireComponentNode) {
            const nameAttribute = this.domNode.getAttribute('name');
            if (nameAttribute) {
                this.block = {
                    name: nameAttribute,
                    props: JSON.parse((_a = this.domNode.getAttribute('attributes')) !== null && _a !== void 0 ? _a : '{}')
                };
            }
        }
    }
    /**
     * Map this node to a React Element that represents this node.
     * @returns React Element that represents this node.
     */
    asReactElement() {
        if (this.element) {
            return this.element;
        }
        this.element = (() => {
            var _a, _b;
            if (this.isSmartfireComponentNode) {
                return createElement(BlockNode, {
                    node: this,
                    key: key++
                });
            }
            if (this.isHTMLBoundaryNode) {
                return createElement(HTMLBoundaryNode, {
                    node: this,
                    disableNestedRendering: this.domNode.hasAttribute('disable-nested-rendering') || ((_a = this.domNode) === null || _a === void 0 ? void 0 : _a.classList.contains('disable-nested-rendering')),
                    key: key++
                });
            }
            if (this.domNode instanceof HTMLElement) {
                return createElement(HTMLNode, {
                    node: this,
                    key: key++
                });
            }
            if (this.domNode instanceof Text) {
                if (((_b = this.domNode.textContent) === null || _b === void 0 ? void 0 : _b.trim()) === '') {
                    return null;
                }
                return createElement(Fragment, {
                    key: key++
                }, this.domNode.textContent);
            }
            return undefined;
        })();
        return this.element;
    }
    /**
     * Shortcut to get the React Elements of all children.
     */
    childrenAsReactElements() {
        return this.children.map(child => child.asReactElement()).filter(child => child !== null);
    }
    /**
     * Shortcut to get the name of the Smartfire Block.
     */
    get componentName() {
        var _a;
        return (_a = this.block) === null || _a === void 0 ? void 0 : _a.name;
    }
    /**
     * Shortcut to get the props of the Smartfire Block.
     */
    get componentProps() {
        var _a;
        return (_a = this.block) === null || _a === void 0 ? void 0 : _a.props;
    }
    /**
     * List all block types that are used in this node and its children.
     */
    get blockTypes() {
        const types = new Set();
        if (this.isSmartfireComponentNode) {
            types.add(this.componentName);
        }
        for (const child of this.children) {
            for (const type of child.blockTypes) {
                types.add(type);
            }
        }
        return Array.from(types);
    }
}

//# sourceMappingURL=BlockEngineNode.js.map
